import { shouldIterate } from "./json"

/**
 * Attempts to get data from an object using dot notation
 * 
 * @param {string} key 
 * @param {Object} value 
 * @param {string:'.'} separator 
 * @returns 
 */
const dataGet = (key, value, separator = '.') => {
    return String(key)
        .split(separator)
        .reduce((currentNestedData, location) => {
            return currentNestedData?.[location] ?? undefined
        }, value)
}

/**
 * Sets data in an object using dot notation
 * 
 * @param {Object} data 
 * @param {string} key 
 * @param {any}    value
 * @param {string} separator 
 * @returns 
 */
const dataSet = (data, key, value, separator = '.') => {

    let currentData = data;
    let keyParts = String(key).split(separator);

    /**
     * Using a for loop so we can make use of an early return for undefined values
     * 
     * -1 so we add the last value manually
     */
    for(let i = 0; i < keyParts.length - 1; i++)
    {

        //If our value is undefined we can't iterate any further into the key as we don't have data
        if(currentData[keyParts[i]] === undefined){
            currentData[keyParts[i]] = {};
        }

        currentData = currentData[keyParts[i]]
    }

    currentData[keyParts[keyParts.length - 1]] = value

    return data
}

/**
 * Converts an object following dot notation into a standard nested object
 * 
 * @param {Object} object 
 * @param {string:'.'} separator 
 * @returns 
 */
const toObject = (object, separator = '.') => {
    return Object.keys(object)
        .reduce((calculatedObject, key) => {

            let split = key.split(separator)
            let calculatedObjectPart = calculatedObject

            split.forEach((keyPart, index) => {
                if(index === split.length - 1){
                    calculatedObjectPart[keyPart] = object[key]
                    return
                }

                if(calculatedObjectPart[keyPart] === undefined){
                    calculatedObjectPart[keyPart] = {}
                }

                calculatedObjectPart = calculatedObjectPart[keyPart]
            })

            return calculatedObject
        }, {})
}

/**
 * Converts a nested object to dot notation
 * 
 * @param {Object} object 
 * @param {String} separator 
 * @param {String|null} prefix 
 * @returns {Object}
 */
const fromObject = (object, separator = '.', prefix = null) => {
    return Object.keys(object)
        .reduce((flattened, key) => {
            if(shouldIterate(object[key])){
                return {
                    ...flattened,
                    ...fromObject(object[key], separator, getKey(key, prefix, separator))
                }
            }

            return {
                ...flattened,
                [getKey(key, prefix, separator)]: object[key] 
            }
        }, {})
}

/**
 * Gets the dot notation version of the field name required
 * 
 * @param {String} key 
 * @param {String|null} prefix 
 * @param {String} separator 
 * @returns {String}
 */
const getKey = (key, prefix = null, separator = '.') => {
    return prefix !== null
        ? `${prefix}${separator}${key}`
        : key
}

export {
    dataGet,
    dataSet,
    toObject,
    fromObject
}